import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter, StickyNote, X } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { formatRelativeTime, useNow } from '../lib/relativeTime';
import { EntityFilterPicker, type FilterEntity, type FilterModes, DEFAULT_FILTER_MODES } from '../components/EntityFilterPicker';

interface NoteItem {
  id: string;
  title: string;
  tag_ids: string[];
  scope_ids: string[];
  created_at: string;
  updated_at: string;
}

interface Tag { id: string; name: string; color: string; }
interface Scope { id: string; name: string; color: string; }

export function NotesPage() {
  const toast = useToast();
  useNow(); // tick every 30s so relative timestamps stay fresh
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterEntities, setFilterEntities] = useState<FilterEntity[]>([]);
  const [filterModes, setFilterModes] = useState<FilterModes>({});

  async function loadData() {
    try {
      const params = new URLSearchParams();

      // Group filterEntities by type and build plural query params
      const byType = new Map<string, string[]>();
      for (const fe of filterEntities) {
        const ids = byType.get(fe.entity_type) ?? [];
        ids.push(fe.entity_id);
        byType.set(fe.entity_type, ids);
      }
      const tagIds = byType.get('tag');
      if (tagIds?.length) params.set('tag_ids', tagIds.join(','));
      const scopeIds = byType.get('scope');
      if (scopeIds?.length) params.set('scope_ids', scopeIds.join(','));

      // AND/OR mode params — only send when non-default
      if (tagIds && tagIds.length > 1) {
        const tagMode = filterModes.tag ?? DEFAULT_FILTER_MODES.tag ?? 'or';
        if (tagMode !== 'and') params.set('tag_mode', tagMode);
      }
      if (scopeIds && scopeIds.length > 1) {
        const scopeMode = filterModes.scope ?? DEFAULT_FILTER_MODES.scope ?? 'or';
        if (scopeMode !== 'or') params.set('scope_mode', scopeMode);
      }

      params.set('sort_by', 'updated_at');
      params.set('sort_dir', 'desc');

      const [n, t, scp] = await Promise.all([
        apiGet<NoteItem[]>(`/notes?${params}`),
        apiGet<Tag[]>('/tags'),
        apiGet<Scope[]>('/scopes'),
      ]);
      setNotes(n);
      setTags(t);
      setScopes(scp);
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [filterEntities, filterModes]);

  async function handleCreate(title: string) {
    try {
      await apiPost('/notes', { title });
      setShowCreate(false);
      toast.success('Note created');
      loadData();
    } catch {
      toast.error('Failed to create note');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this note?')) return;
    try {
      await apiDelete(`/notes/${id}`);
      toast.success('Note deleted');
      loadData();
    } catch {
      toast.error('Failed to delete note');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  const tagMap = new Map(tags.map(t => [t.id, t]));
  const scopeMap = new Map(scopes.map(s => [s.id, s]));

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Notes</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Note
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Filter size={14} className="text-text-muted" />
        <EntityFilterPicker
          selected={filterEntities}
          onChange={setFilterEntities}
          allowedTypes={['tag', 'scope']}
          placeholder="Filter by tags, projects..."
          filterModes={filterModes}
          onFilterModesChange={setFilterModes}
        />
        {(filterEntities.length > 0 || Object.keys(filterModes).length > 0) && (
          <button onClick={() => { setFilterEntities([]); setFilterModes({}); }} className="text-xs text-text-muted hover:text-text-primary">
            Clear filter
          </button>
        )}
      </div>

      {/* Note List */}
      {notes.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <StickyNote size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No notes found</p>
          <p className="text-sm">Create a note to get started.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {notes.map((note) => (
            <div key={note.id} className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group">
              <StickyNote size={16} className="text-[#14b8a6] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <Link
                  to={`/notes/${note.id}`}
                  className="text-sm text-text-primary hover:text-accent transition-colors"
                >
                  {note.title}
                </Link>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-text-muted">
                    {formatRelativeTime(note.updated_at)}
                  </span>
                  {note.tag_ids.map(tid => {
                    const tag = tagMap.get(tid);
                    return tag ? (
                      <span key={`t-${tid}`} className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                        {tag.name}
                      </span>
                    ) : null;
                  })}
                  {note.scope_ids.map(sid => {
                    const scope = scopeMap.get(sid);
                    return scope ? (
                      <span key={`s-${sid}`} className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: scope.color + '20', color: scope.color }}>
                        {scope.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
              <button
                onClick={() => handleDelete(note.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateNoteModal onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </div>
    </div>
  );
}

function CreateNoteModal({
  onSave,
  onClose,
}: {
  onSave: (title: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New Note</h2>
        <div>
          <label className="block text-sm text-text-secondary mb-1">Title *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
            placeholder="What's this note about?"
            onKeyDown={e => { if (e.key === 'Enter' && title.trim()) onSave(title.trim()); }}
            className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button
            onClick={() => title.trim() && onSave(title.trim())}
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

